import React, { useEffect, useRef, useState } from 'react';

interface AudioIntroductionProps {
  className?: string;
}

const AudioIntroduction: React.FC<AudioIntroductionProps> = ({ className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Toggle transcript visibility
  const toggleTranscript = () => {
    setShowTranscript(!showTranscript);
  };
  
  // Handle play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      const playPromise = audioRef.current.play();
      
      // Handle autoplay restrictions
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Audio playback error:", error);
          // Reset play state if autoplay was blocked
          setIsPlaying(false);
        });
      }
    }
  };
  
  // Handle play/pause state
  const handlePlay = () => {
    setIsPlaying(true);
  };
  
  const handlePause = () => {
    setIsPlaying(false);
  };
  
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Preload the audio when component mounts
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, []);
  
  return (
    <div className={`my-4 p-4 bg-[#C2A4D6]/10 rounded-lg flex flex-col gap-3 ${className}`}>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <button 
            onClick={togglePlayPause}
            className="relative w-12 h-12 rounded-full bg-[#C2A4D6] flex items-center justify-center hover:bg-[#b390c9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C2A4D6] focus:ring-opacity-50 shadow-lg"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <span className="text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
              </span>
            ) : (
              <span className="text-white ml-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              </span>
            )}
          </button>
          
          <div>
            <h4 className="font-medium text-[#C2A4D6]">Cora</h4>
            <p className="text-sm text-gray-600">
              {isPlaying ? "Speaking..." : "Your AI companion"}
            </p>
          </div>
        </div>
        
        <div className="mb-2">
          <input
            type="range"
            min="0"
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-2 accent-[#C2A4D6] bg-white rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        <audio 
          ref={audioRef}
          preload="metadata"
          className="hidden"
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handlePause}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        >
          <source src="/assets/audio/cora-introduction.mp3" type="audio/mp3" />
          Your browser does not support audio playback.
        </audio>
        
        {/* Audio visualization and transcript removed for a cleaner look */}
      </div>
    </div>
  );
};

export default AudioIntroduction;